'use client';

import { use, useEffect, useState } from 'react';
import { 
  getCurrentScreenStatus, 
  setScreenDimensions, 
  setDevicePixelRatio,
  setScreenOrientation,
  type ScreenStatus,
  type ScreenOrientation,
  CHECK_DESKTOP_ZOOM // Import the global config
} from '@/utils/globalState';

interface ScreenAwareContentProps {
  supportedContent: React.ReactNode;
  fallbackContent: React.ReactNode;
  mobileSupportLandscape?: boolean;
  mobileSupportPortrait?: boolean;
  checkDesktopZoom?: boolean; // Prop to override global setting
}

// Tolerance for devicePixelRatio deviation from 1.0
const PIXEL_RATIO_TOLERANCE = 0.05;

// Default screen status for server-side rendering
const defaultScreenStatus: ScreenStatus = {
  isSupported: true, // Assume supported until client-side can verify
  type: 'desktop',
  width: null,
  height: null,
  pixelRatio: null,
  orientation: 'unknown',
  zoomDetected: false
};

export default function ScreenAwareContent({
  supportedContent,
  fallbackContent,
  mobileSupportLandscape = false,
  mobileSupportPortrait = true,
  checkDesktopZoom, // This can be undefined to use global setting
}: ScreenAwareContentProps) {
  // Use the prop if provided, otherwise fall back to global setting
  const shouldCheckZoom = checkDesktopZoom !== undefined ? checkDesktopZoom : CHECK_DESKTOP_ZOOM;
  
  const [screenStatus, setScreenStatus] = useState<ScreenStatus>(() => {
    // Attempt to get initial status directly
    return getCurrentScreenStatus(); 
  });
  
  useEffect(() => {
    const updateStatus = (event?: Event) => {
      setScreenStatus(getCurrentScreenStatus());
    };

    // Update status *after* mount
    updateStatus(); 
    
    // Listen for resize and custom event
    window.addEventListener('resize', updateStatus);
    window.addEventListener('screen-state-change', updateStatus); 
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', updateStatus);
      window.removeEventListener('screen-state-change', updateStatus);
    };
  }, []);

  let isDeviceSupported = false;
  
  if (screenStatus.isSupported) {
    if (screenStatus.type === 'desktop') {
      // For desktop, check zoom only if enabled (prop or global config)
      isDeviceSupported = shouldCheckZoom ? !screenStatus.zoomDetected : true;
    } else if (screenStatus.type === 'mobile') {
      // For mobile, only check orientation support (ignore zoom)
      if (screenStatus.orientation === 'landscape' && mobileSupportLandscape) {
        isDeviceSupported = true;
      } else if (screenStatus.orientation === 'portrait' && mobileSupportPortrait) {
        isDeviceSupported = true;
      }
    }
  }
  
  return isDeviceSupported ? <>{supportedContent}</> : <>{fallbackContent}</>;
} 